import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Play, Pause, ChevronRight, Music, BookOpen, Landmark } from 'lucide-react';
import { API_URL } from '../constants';

// ─── Sub-pages ───────────────────────────────────────────────────────────────

const AnthemSection = ({ MEDIA }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const toggle = () => {
    if (!audioRef.current) return;
    isPlaying ? audioRef.current.pause() : audioRef.current.play();
    setIsPlaying(p => !p);
  };
  return (
    <div className="space-y-8">
      <div className="bg-emerald-700 text-white rounded-2xl p-8">
        <div className="flex items-center gap-4 mb-4">
          <Music className="w-10 h-10" />
          <div>
            <h2 className="text-3xl font-bold">Gbongan Anthem</h2>
            <p className="text-emerald-200">The song that unites our community...</p>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-6">
          <button onClick={toggle} className="flex items-center gap-2 bg-white text-emerald-700 px-5 py-2.5 rounded-xl font-semibold hover:bg-emerald-50 transition">
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isPlaying ? 'Pause Anthem' : 'Play Anthem'}
          </button>
          <audio ref={audioRef} src={`${MEDIA}/audios/Gbongan_anthem.mp3`} onEnded={() => setIsPlaying(false)} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Gbongan Anthem(Yoruba)</h3>
          <div className="space-y-2 text-gray-700 italic text-sm leading-loose">
            <p>Abiodun Adegoriolu </p>
              <p>Alafin to papoda</p>
              <p>O fi opo omo saye</p>
              <p>Okan I’Olufioye</p>
              <p>O si nfe lati ropo re</p>
              <p>Sugbon ko ri owo mu</p>
              <p>O si fi ‘lu Oyo sile</p>
              <p>Pel’awon opo ore</p>
              <p>2</p>
              <p>Won gba ona Igbori lo</p>
              <p>Won si satipo nibe</p>
              <p>Won tun fi Igbori sile</p>
              <p>Won si gba ona Songbe</p>
              <p>Lati Songbe wa Gbongan’le</p>
              <p>Nibe Olufi dade</p>
              <p>Alaanu at’Akinjole</p>
              <p>Ni orisun omi re.</p>
              <p>3</p>
              <p>Olufi labe Ibadan</p>
              <p>Ko tun le de ade re</p>
              <p>Ijoba oselu pase</p>
              <p>Olufi tun dalade</p>
              <p>Olufi Arokodeja</p>
              <p>E sise ‘gboko re ga</p>
              <p>E si ja ki e si segun</p>
              <p>E o je ere pupo.</p>
              <p>~</p>
              <p>~ Lati Owo (Dr) J.O.O. Ojeleye</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">English Translation</h3>
          <div className="space-y-2 text-gray-700 text-sm leading-loose">
            <p>Abiodun Adegoriolu</p>
            <p>Alafin that passed away,</p>
            <p>Many children succeeded him,</p>
            <p>One was Olufioye</p>
            <p>He attempted to be on throne,</p>
            <p>But failed in his ambition</p>
            <p>He had to leave Oyo township;</p>
            <p>Many friends too followed him</p>
            <p>2</p>
            <p>They journeyed through Igbori route</p>
            <p>And they sojourned in the place</p>
            <p>And later they left Igbori</p>
            <p>They journeyed then through Songbe</p>
            <p>From Songbe to Gbongan’le</p>
            <p>There Olufi wore his crown</p>
            <p>Alaanu and Akinjole</p>
            <p>Water sources for the land</p>
            <p>3</p>
            <p>Olufi under Ibadan</p>
            <p>Could not wear his crown again</p>
            <p>Later, government gave an order</p>
            <p>Olufi could wear his crown</p>
            <p>Olufi Arokodeja</p>
            <p>Labour, place her name on map</p>
            <p>Be relentless, win the battle</p>
            <p>Your labour will yield much gain</p>
            <p>~  </p>
            <p>~ Composed by Overseer (Dr) J.O.O. Ojeleye</p>
          </div>
        </div>
      </div>

      
      <div className="bg-white rounded-2xl shadow-md p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-4">About Our Anthem</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          The Gbongan anthem was composed to unite our community and celebrate our shared heritage. 
          Composed by Pastor Okiki Jesu, the anthem speaks of our commitment to progress, unity, 
          and the preservation of our rich cultural legacy.
        </p>
        <p className="text-gray-700 leading-relaxed">
          The anthem is sung at community events, gatherings, and celebrations, serving as a powerful 
          reminder of our shared identity and aspirations as sons and daughters of Gbongan.
        </p>
      </div>
    </div>
    
  );
};

const OrikiSection = ({ MEDIA }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const toggle = () => {
    if (!audioRef.current) return;
    isPlaying ? audioRef.current.pause() : audioRef.current.play();
    setIsPlaying(p => !p);
  };
  return (
    <div className="space-y-8">
      <div className="bg-purple-700 text-white rounded-2xl p-8">
        <div className="flex items-center gap-4 mb-4">
          <span className="text-4xl">🎙️</span>
          <div>
            <h2 className="text-3xl font-bold">Oriki Gbongan</h2>
            <p className="text-purple-200">Traditional praise poetry and oral history</p>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-6">
          <button onClick={toggle} className="flex items-center gap-2 bg-white text-purple-700 px-5 py-2.5 rounded-xl font-semibold hover:bg-purple-50 transition">
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isPlaying ? 'Pause Oriki' : 'Listen to Oriki'}
          </button>
          <audio ref={audioRef} src={`${MEDIA}/audios/gbongan_oriki.mp3`} onEnded={() => setIsPlaying(false)} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Oriki Gbongan (Yoruba)</h3>
          <div className="space-y-2 text-gray-700 italic text-sm leading-loose">
            <p>Omo Olufi Arogodija,</p>
            <p>Omo Olupe,</p>
            <p>Omo Agesinsawo,</p>
            <p>Omo Alebiosu ma fara tigi,</p>
            <p>Omo Afinju eye tii je ni gbangba oko,</p>
            <p>Omo kulodo Awubi,</p>
            <p>Omo kulodo to ti ‘Gbori wa,</p>
            <p>Ara Igbori won ko l’odo,</p>
            <p>Omi Iku ni won on pon mu.</p>
            <p>Tan laasa?,</p>
            <p>Tan Leekoro</p>
            <p>Tan ni Dobode Omi Ipakun,</p>
            <p>Tan l’Afunlele omi Ayaba</p>
            <p>Dobode ko deni lehin ese mo</p>
            <p>E w’Afunlele onigbori won ko lagba</p>
            <p>Ara Igbori ko lagba,</p>
            <p>Gbogbo won nii je baba</p>
            <p>Omo aje baba ma j’Arugbo</p>
            <p>Ara Igbori e ku moko Apinni</p>
            <p>Eni ara ni tii ba won jojo opa</p>
            <p>Omo Aruku r’oja mata</p>
            <p>Oku taa gbe r’oja ti o ta</p>
            <p>Agboku ohun wa’le</p>
            <p>O waa yo yo yo</p>
            <p>O yo ku bi Okinni</p>
            <p>Okinni naa wa n be lehin oosaa kire</p>
            <p>N ni baba yin fi n sewin-in-bo</p>
            <p>Ara Igbori ni omo isin l’ode Ijan</p>
            <p>Osan re, re re l’ojude Iwoye</p>
            <p>Araba tengbe tengbe l’ojude Songbe</p>
            <p>Idi Igba l’oja</p>
            <p>Songbe n’ile</p>
            <p>A jo’lu j’Latç</p>
            <p>Songbe n’ile</p>
            <p>A jo’lu gberin</p>
            <p>A jo’lu gberin</p>
            <p>A jo koso</p>
            <p>A j’aro</p>
            <p>A si jo Abente baba awon ilu</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">English Translation</h3>
          <div className="space-y-2 text-gray-700 text-sm leading-loose">
            <p>Son of Olufi Arogodija</p>
            <p>Son of Olupe,</p>
            <p>Son of Agesinsawo,</p>
            <p>Son of Alebiosu, who doesn't rest on the tree,</p>
            <p>Son of Afinju, the bird that ate in the open field,</p>
            <p>Son of kulodo Awubi,</p>
            <p>Son of kulodo who came from ‘Gbori,</p>
            <p>The Igbori people do not have a river,</p>
            <p>SO they drank the water of death.</p>
            <p>Who owns asa?,</p>
            <p>Who owns ekoro</p>
            <p>Who owns Dobode Omi Ipakun,</p>
            <p>Who owns Afunlele the water of the Queen</p>
            <p>...</p>
            <p>The people of Igbori doesn't have elders,</p>
            <p>All of them are fathers</p>
            <p>The descendants of those called Father, but not called Aged</p>
            <p>...</p>
            <p>The son of Aruku who went to market that cant make a sale</p>
            <p>The dead that we carried to market that cant make a sale</p>
            <p>We brought the dead home</p>
            <p>He then shrink</p>
            <p>until it became a needle</p>
            <p>The needle is behind the Ikire deity</p>
            <p>...</p>
            <p>Idi Igba is the market</p>
            <p>Songbe is home</p>
            <p>...</p>
            <p>We danced koso</p>
            <p>We danced aro</p>
            <p>We danced Abente, the father of drums</p>
          </div>
        </div>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6">
        <h4 className="font-bold text-gray-900 mb-3">About Oriki</h4>
        <p className="text-gray-700 text-sm leading-relaxed mb-3">
          Oriki represents the oral tradition of our people, preserving history, lineage, and the unique cultural identity passed down through generations.
          Each family in Gbongan has a unique oriki that celebrates their ancestry and achievements.
        </p>
        <p className="text-gray-700 text-sm leading-relaxed">
          We are actively documenting and preserving the oriki of Gbongan families. If you know your family oriki, please consider sharing it for inclusion in our archive.
        </p>
        <Link to="/contribute/cultural" className="mt-4 inline-block px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 transition text-sm">Share Your Oriki</Link>
      </div>
    </div>
  );
};

// ─── History Sub-sections ─────────────────────────────────────────────────────

const defaultHistory = [
  { id: 'h1', title: 'Early History of Gbongan', content: 'Gbongan is neither a contraption of different identities nor a settlement for displaced people from the 18th-century wars that swept through Yorubaland and the Oyo Empire. \nLocated along Latitude 07°15.3’ and Longitude 04°15.8’, it lies within the humid rainforest region of Nigeria, with distinct dry (November–March) and wet (April–October) seasons. \n\nAccording to its king, the town was an offshoot of old Oyo, founded by a direct son of Alaafin Abiodun Adegorolu, who was forced out of town after losing his bid for the throne. \n Olufioye, a prince and son of Alaafin Abiodun, contested for the throne but lost. Tradition required that he leave Oyo. He left with followers, including a hunter named Oluoje. They migrated through Igbori (between Ogbomoso and Igbeti), then to Songbe in present-day Egbedore LGA, Osun State. Due to hardship, they moved again under guidance from an oracle and a spirit called Oro, believed to dwell in trees. They were told to settle wherever the spirit disappeared. The spirit eventually disappeared at Gbongan-Ile, between Gbongan and Ode-Omu. \nThere, Olufioye was crowned king, as his followers already recognized him as Oba.  \n\n~(Article is culled from The Nation Newspapers, publication dated 07th May 2014)' },
  { id: 'h2', title: 'The Olufi Monarchy', content: 'The traditional ruler of Gbongan bears the title "Olufi of Gbongan." The monarchy has played a central role in maintaining peace, culture, and governance in the community for generations. In those days, there were no ruling houses or government involvement. Selection was done among qualified descendants of the Olufi lineage.' },
  { id: 'h3', title: 'Modern Gbongan', content: "Today, Gbongan is a vibrant community that honors its past while embracing progress. The town has produced notable scholars, professionals, and leaders who have contributed to Nigeria's development." },
  { id: 'h4', title: 'Past Kings of Gbongan & its ruling houses', content: "Several past Olufis have made significant contributions: \nOba Fagbola, 1825-1835, from Fagbola Ruling House. \nOba Olujide (1), 1835-1859, from Olujide Ruling House. \nOba Ifaromade Akinnu, 1860-1913, from Sooko Ruling House. \nOba Oloyede, 1913-1924, from Ajagbogbo Ruling House. \nOba Adewale Asabi, 1926-1948, from Asabi Olugboira Ruling House \nOba Joseph Oyeniyi Makanjuola, 1948-1971, (Fagbola 2) from Fagbola Ruling House. \nOba Jacob Adegun Adeoye, 1973-1988, from Olujide Ruling House. \nOba Dr. Solomon Oyewole Babayemi (Ph.D, JP), Akinrinola 1, 1988-1997 from Akinrinola Ruling House. \nOba Dr. Adetoyese Oyeniyi (Ph.D, JP), (Odugbemi 1) 1998 - Date from Odugbemi Ruling House." },
  { id: 'h5', title: 'Olufi-In-Council Chiefs', content: "High Chief (Alhaji) Quasim Ayodele Onikepe - Balogun \nHigh Chief Johnson Oyelami Dare - Otun Olufi \nHigh Chief Mikahil Olabamiji Adisa - Oosa \nHigh Chief Tajudeen Ajao Yusuf - Aro \nHigh Chief Jimoh Egbewale - Odofin \nHigh Chief Ibrahim Adedayo Ayinde - Agbaakin  \nHigh Chief Emmanuel Adewuyi Adetunji - Otun Balogun \nHigh Chief Ambaliyu Onifade - Osi-Otun-Olufi \nHigh Chief Augustus Koledoye Asabi - Asalu \nHigh Chief Oladokun Ayanwale - Osi-Balogun " },
];

const historyColors = [
  'bg-amber-50 border-amber-400',
  'bg-emerald-50 border-emerald-400',
  'bg-blue-50 border-blue-400',
  'bg-purple-50 border-purple-400',
];

const EarlyHistory = ({ entries }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const displayHistory = entries.length > 0 ? entries : defaultHistory.slice(0, 1);
  return (
    <div className="space-y-3">
      {displayHistory.map((entry, idx) => {
        const key = `h-${entry.id || idx}`;
        const expanded = !!expandedItems[key];
        return (
          <div key={key} className={`${historyColors[idx % historyColors.length]} border-l-4 rounded-xl overflow-hidden`}>
            <button onClick={() => setExpandedItems(p => ({ ...p, [key]: !p[key] }))} className="w-full flex items-center justify-between px-6 py-4 text-left hover:brightness-95 transition">
              <h4 className="text-lg font-semibold text-gray-900">{entry.title}</h4>
              <span className={`text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>▼</span>
            </button>
            {expanded && (
              <div className="px-6 pb-5">
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{entry.content}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const OlufiMonarchy = ({ entries }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const displayHistory = entries.length > 0 ? entries : defaultHistory.slice(1, 2);
  return (
    <div className="space-y-3">
      {displayHistory.map((entry, idx) => {
        const key = `h-${entry.id || idx}`;
        const expanded = !!expandedItems[key];
        return (
          <div key={key} className={`${historyColors[(idx + 1) % historyColors.length]} border-l-4 rounded-xl overflow-hidden`}>
            <button onClick={() => setExpandedItems(p => ({ ...p, [key]: !p[key] }))} className="w-full flex items-center justify-between px-6 py-4 text-left hover:brightness-95 transition">
              <h4 className="text-lg font-semibold text-gray-900">{entry.title}</h4>
              <span className={`text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>▼</span>
            </button>
            {expanded && (
              <div className="px-6 pb-5">
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{entry.content}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ModernGbongan = ({ entries }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const displayHistory = entries.length > 0 ? entries : defaultHistory.slice(2, 3);
  return (
    <div className="space-y-3">
      {displayHistory.map((entry, idx) => {
        const key = `h-${entry.id || idx}`;
        const expanded = !!expandedItems[key];
        return (
          <div key={key} className={`${historyColors[(idx + 2) % historyColors.length]} border-l-4 rounded-xl overflow-hidden`}>
            <button onClick={() => setExpandedItems(p => ({ ...p, [key]: !p[key] }))} className="w-full flex items-center justify-between px-6 py-4 text-left hover:brightness-95 transition">
              <h4 className="text-lg font-semibold text-gray-900">{entry.title}</h4>
              <span className={`text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>▼</span>
            </button>
            {expanded && (
              <div className="px-6 pb-5">
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{entry.content}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const pastKingsData = [
  { name: 'Oba Fagbola', reign: '1825–1835', house: 'Fagbola Ruling House', img: '/1_oluf.jpeg' },
  { name: 'Oba Olujide (I)', reign: '1835–1859', house: 'Olujide Ruling House', img: '/1_oluf.jpeg' },
  { name: 'Oba Ifaromade Akinnu', reign: '1860–1913', house: 'Sooko Ruling House', img: '/1_oluf.jpeg' },
  { name: 'Oba Oloyede', reign: '1913–1924', house: 'Ajagbogbo Ruling House', img: '/1_oluf.jpeg' },
  { name: 'Oba Adewale Asabi', reign: '1926–1948', house: 'Asabi Olugboira Ruling House', img: '/1_oufi.jpeg' },
  { name: 'Oba Joseph Oyeniyi Makanjuola', reign: '1948–1971', house: 'Fagbola Ruling House (Fagbola II)', img: '/1_oluf.jpeg' },
  { name: 'Oba Jacob Adegun Adeoye', reign: '1973–1988', house: 'Olujide Ruling House', img: '/1_oluf.jpeg' },
  { name: 'Oba Dr. Solomon Oyewole Babayemi', reign: '1988–1997', house: 'Akinrinola Ruling House (Akinrinola I)', img: '/1_oluf.jpeg' },
  { name: 'Oba Dr. Adetoyese Oyeniyi', reign: '1998–Date', house: 'Odugbemi Ruling House (Odugbemi I)', img: '/1_olufi.jpeg' },
];

const PastOlufis = ({ entries }) => {
  const [expanded, setExpanded] = useState(false);
  const display = entries.length > 0 ? entries : defaultHistory.slice(3, 4);
  const entry = display[0];
  const colorClass = historyColors[3 % historyColors.length];
  return (
    <div className="space-y-3">
      <div className={`${colorClass} border-l-4 rounded-xl overflow-hidden`}>
        <button onClick={() => setExpanded(p => !p)} className="w-full flex items-center justify-between px-6 py-4 text-left hover:brightness-95 transition">
          <h4 className="text-lg font-semibold text-gray-900">{entry.title}</h4>
          <span className={`text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>▼</span>
        </button>
        {expanded && (
          <div className="px-6 pb-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-2">
              {pastKingsData.map((king, i) => (
                <div key={i} className="flex flex-col items-center bg-white rounded-xl shadow-sm p-3 text-center">
                  <img
                    src={king.img}
                    alt={king.name}
                    className="w-20 h-24 object-cover rounded-lg shadow mb-2"
                  />
                  <p className="text-xs font-bold text-gray-900 leading-tight">{king.name}</p>
                  <p className="text-xs text-emerald-700 font-semibold mt-0.5">{king.reign}</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-tight">{king.house}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const chiefsData = [
  { title: 'Balogun', name: 'High Chief (Alhaji) Quasim Ayodele Onikepe', img: '/2_balogun.jpeg' },
  { title: 'Otun Olufi', name: 'High Chief Johnson Oyelami Dare', img: '/4_otun_olufi.jpeg' },
  { title: 'Oosa', name: 'High Chief Mikahil Olabamiji Adisa', img: '/3_oosa.jpeg' },
  { title: 'Aro', name: 'High Chief Tajudeen Ajao Yusuf', img: '/5_aro.jpeg' },
  { title: 'Odofin', name: 'High Chief Jimoh Egbewale', img: '/6_odofin.jpeg' },
  { title: 'Agbaakin', name: 'High Chief Ibrahim Adedayo Ayinde', img: '/7_agbaakin.jpeg' },
  { title: 'Otun Balogun', name: 'High Chief Emmanuel Adewuyi Adetunji', img: '/8_otun_balogun.jpeg' },
  { title: 'Osi-Otun-Olufi', name: 'High Chief Ambaliyu Onifade', img: '/9_osi_otun_olufi.jpeg' },
  { title: 'Asalu', name: 'High Chief Augustus Koledoye Asabi', img: '/10_asalu.jpeg' },
  { title: 'Osi-Balogun', name: 'High Chief Oladokun Ayanwale', img: '/11_osi_balogun.jpeg' },
];

const Chiefs = ({ entries }) => {
  const [expanded, setExpanded] = useState(false);
  const display = entries.length > 0 ? entries : defaultHistory.slice(4, 5);
  const entry = display[0];
  const colorClass = historyColors[0 % historyColors.length];
  return (
    <div className="space-y-3">
      <div className={`${colorClass} border-l-4 rounded-xl overflow-hidden`}>
        <button onClick={() => setExpanded(p => !p)} className="w-full flex items-center justify-between px-6 py-4 text-left hover:brightness-95 transition">
          <h4 className="text-lg font-semibold text-gray-900">{entry.title}</h4>
          <span className={`text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>▼</span>
        </button>
        {expanded && (
          <div className="px-6 pb-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-2">
              {chiefsData.map((chief, i) => (
                <div key={i} className="flex flex-col items-center bg-white rounded-xl shadow-sm p-3 text-center">
                  <img
                    src={chief.img}
                    alt={chief.name}
                    className="w-20 h-24 object-cover rounded-lg shadow mb-2"
                  />
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">{chief.title}</p>
                  <p className="text-xs text-gray-700 mt-0.5 leading-tight">{chief.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const OurHistorySection = ({ entries }) => (
  <div className="space-y-3">
    <EarlyHistory entries={entries.filter(e => e.id === 'h1' || e.history_era === 'early')} />
    <OlufiMonarchy entries={entries.filter(e => e.id === 'h2' || e.history_era === 'monarchy')} />
    <ModernGbongan entries={entries.filter(e => e.id === 'h3' || e.history_era === 'modern')} />
    <PastOlufis entries={entries.filter(e => e.id === 'h4' || e.history_era === 'past')} />
    <Chiefs entries={entries.filter(e => e.id === 'h5' || e.history_era === 'chiefs')} />
  </div>
);

// ─── Heritage Sub-sections ────────────────────────────────────────────────────

const defaultHeritage = [
  { id: 'he1', title: 'Cultural Festivals', content: 'Gbongan observes several important cultural festivals that bring the community together, celebrating heritage and giving thanks for blessings received. The major festivals are: \nOlufi day \nMasquarades from different houses \nYam Festials etc' },
  { id: 'he2', title: 'Traditional Institutions', content: "The traditional institutions of Gbongan, including the Olufi's palace and council of chiefs, remain vital to community governance and cultural preservation." },
  { id: 'he3', title: 'Arts and Crafts', content: 'Gbongan has a rich tradition of arts and crafts including weaving, pottery, and woodcarving that have been passed down through generations.' },
];

const heritageColors = [
  'bg-rose-50 border-rose-400',
  'bg-teal-50 border-teal-400',
  'bg-indigo-50 border-indigo-400',
  'bg-orange-50 border-orange-400',
];

const HeritageCard = ({ entry, colorClass, MEDIA }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`${colorClass} border-l-4 rounded-xl overflow-hidden`}>
      <button onClick={() => setExpanded(p => !p)} className="w-full flex items-center justify-between px-6 py-4 text-left hover:brightness-95 transition">
        <h4 className="text-base font-semibold text-gray-900">{entry.title}</h4>
        <span className={`text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {expanded && (
        <div className="px-6 pb-5">
          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{entry.content}</p>
          {entry.image && <img src={entry.image.startsWith('http') ? entry.image : `${MEDIA}${entry.image}`} alt={entry.title} className="mt-4 rounded-lg max-w-full shadow" />}
        </div>
      )}
    </div>
  );
};

const CulturalFestivals = ({ entries, MEDIA }) => {
  const display = entries.length > 0 ? entries : defaultHeritage.slice(0, 1);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {display.map((entry, idx) => (
        <HeritageCard key={`he-${entry.id || idx}`} entry={entry} colorClass={heritageColors[idx % heritageColors.length]} MEDIA={MEDIA} />
      ))}
    </div>
  );
};

const TraditionalInstitutions = ({ entries, MEDIA }) => {
  const display = entries.length > 0 ? entries : defaultHeritage.slice(1, 2);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {display.map((entry, idx) => (
        <HeritageCard key={`he-${entry.id || idx}`} entry={entry} colorClass={heritageColors[(idx + 1) % heritageColors.length]} MEDIA={MEDIA} />
      ))}
    </div>
  );
};

const ArtsAndCrafts = ({ entries, MEDIA }) => {
  const display = entries.length > 0 ? entries : defaultHeritage.slice(2, 3);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {display.map((entry, idx) => (
        <HeritageCard key={`he-${entry.id || idx}`} entry={entry} colorClass={heritageColors[(idx + 2) % heritageColors.length]} MEDIA={MEDIA} />
      ))}
    </div>
  );
};

const CulturalHeritageSection = ({ entries, MEDIA }) => (
  <div className="space-y-4">
    <CulturalFestivals entries={entries.filter(e => e.id === 'he1' || e.heritage_type === 'festivals')} MEDIA={MEDIA} />
    <TraditionalInstitutions entries={entries.filter(e => e.id === 'he2' || e.heritage_type === 'institutions')} MEDIA={MEDIA} />
    <ArtsAndCrafts entries={entries.filter(e => e.id === 'he3' || e.heritage_type === 'arts')} MEDIA={MEDIA} />
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const HistoryHeritagePage = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const [culturalContent, setCulturalContent] = useState([]);
  const MEDIA = process.env.REACT_APP_MEDIA_URL || '/media';

  useEffect(() => {
    fetch(`${API_URL}/cultural-content/?page_size=100`).then(r => r.json()).then(d => {
      setCulturalContent(Array.isArray(d) ? d : (d.results || []));
    }).catch(() => {});
  }, []);

  const historyEntries = culturalContent.filter(c => c.content_type === 'history' && c.is_published !== false);
  const heritageEntries = culturalContent.filter(c => c.content_type === 'heritage' && c.is_published !== false);

  const tabs = [
    { key: undefined, label: 'History & Culture', icon: <Landmark className="w-4 h-4" /> },
    { key: 'anthem', label: 'Anthem', icon: <Music className="w-4 h-4" /> },
    { key: 'oriki', label: 'Oriki', icon: <span className="text-base">🎙️</span> },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-emerald-600">Home</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium">History & Heritage</span>
        {section && <><ChevronRight className="w-4 h-4" /><span className="text-gray-900 capitalize">{section}</span></>}
      </nav>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-8 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.key || 'main'}
            onClick={() => navigate(tab.key ? `/history-heritage/${tab.key}` : '/history-heritage')}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition -mb-px ${
              (section === tab.key || (!section && !tab.key))
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Anthem sub-page */}
      {section === 'anthem' && <AnthemSection MEDIA={MEDIA} />}

      {/* Oriki sub-page */}
      {section === 'oriki' && <OrikiSection MEDIA={MEDIA} />}

      {/* Main History & Heritage */}
      {!section && (
        <div className="space-y-10">
          <div className="flex items-center gap-4">
            <span className="text-5xl">📜</span>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">History & Cultural Heritage</h1>
              <p className="text-gray-600">Exploring the rich history and traditions of Gbongan</p>
            </div>
          </div>

          {/* Quick access to Anthem & Oriki */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link to="/history-heritage/anthem" className="bg-emerald-700 text-white rounded-2xl p-6 flex items-center gap-4 hover:bg-emerald-800 transition">
              <Music className="w-10 h-10" />
              <div>
                <h3 className="font-bold text-lg">Gbongan Anthem</h3>
                <p className="text-emerald-200 text-sm">Listen to our community anthem</p>
              </div>
              <ChevronRight className="w-5 h-5 ml-auto" />
            </Link>
            <Link to="/history-heritage/oriki" className="bg-purple-700 text-white rounded-2xl p-6 flex items-center gap-4 hover:bg-purple-800 transition">
              <span className="text-4xl">🎙️</span>
              <div>
                <h3 className="font-bold text-lg">Oriki Gbongan</h3>
                <p className="text-purple-200 text-sm">Traditional praise poetry</p>
              </div>
              <ChevronRight className="w-5 h-5 ml-auto" />
            </Link>
          </div>

          {/* Our History */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2"><BookOpen className="w-6 h-6 text-emerald-600" />Our History</h2>
            <OurHistorySection entries={historyEntries} />
          </div>

          {/* Cultural Heritage */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2"><Landmark className="w-6 h-6 text-emerald-600" />Cultural Heritage</h2>
            <CulturalHeritageSection entries={heritageEntries} MEDIA={MEDIA} />
          </div>

          {/* Preservation Banner */}
          <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Preservation Initiatives</h3>
            <p className="text-gray-700 leading-relaxed">
              The Gbongan community is committed to preserving and promoting our rich cultural heritage.
              Through documentation, education, and active celebration of our traditions, we ensure that
              future generations remain connected to their roots while contributing to modern society.
            </p>
            <Link to="/contribute/archive" className="mt-5 inline-block px-5 py-2.5 bg-emerald-700 text-white rounded-xl hover:bg-emerald-800 transition font-medium">Contribute to Heritage</Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryHeritagePage;